import { z } from 'zod';
export const uuidSchema = z.uuid();
export const visibilitySchema = z.enum(['public', 'followers', 'private']);
export const staffRoleSchema = z.enum(['member', 'moderator', 'editor', 'administrator', 'owner']);
export const accountRoleSchema = z.enum([
  'member',
  'business',
  'moderator',
  'editor',
  'administrator',
  'owner',
]);
export const httpsUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .transform((value, ctx) => {
    try {
      const url = new URL(value);
      if (
        url.protocol !== 'https:' ||
        url.username ||
        url.password ||
        !url.hostname.includes('.') ||
        url.hostname === 'localhost' ||
        /^(127\.|0\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/.test(url.hostname)
      )
        throw new Error();
      url.hash = '';
      return url.toString();
    } catch {
      ctx.addIssue({ code: 'custom', message: 'Enter a public HTTPS URL without credentials.' });
      return z.NEVER;
    }
  });
const text = (max: number) => z.string().trim().max(max);
/** Root paths used by the application and its public service endpoints. */
export const RESERVED_PROFILE_NAMES = Object.freeze([
  'about',
  'account',
  'accounts',
  'admin',
  'api',
  'assets',
  'auth',
  'catalog',
  'cdn',
  'companies',
  'contact',
  'discover',
  'equipment',
  'favicon',
  'flexes',
  'forum',
  'forums',
  'gym',
  'gyms',
  'health',
  'help',
  'home',
  'legal',
  'login',
  'logout',
  'manifest',
  'media',
  'member',
  'members',
  'messages',
  'my-gym',
  'mygym',
  'notifications',
  'owner',
  'policies',
  'posts',
  'pricing',
  'privacy',
  'profile',
  'profiles',
  'readiness',
  'register',
  'reviews',
  'robots',
  'search',
  'security',
  'settings',
  'signin',
  'signup',
  'sitemap',
  'static',
  'status',
  'stories',
  'support',
  'terms',
  'trade',
  'trades',
  'uploads',
  'well_known',
  'www',
]);
export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(30)
  .regex(/^[a-z][a-z0-9_]*$/, 'Use letters, numbers and underscores.')
  .refine((name) => !RESERVED_PROFILE_NAMES.includes(name), 'That profile name is reserved.');
export const profileUpdateSchema = z
  .object({
    // Only the database can authorize keeping the provisional name bound to this profile's UUID.
    username: usernameSchema.or(z.string().regex(/^member_[a-f0-9]{32}$/)),
    display_name: text(80).min(1),
    bio: text(1000).default(''),
    location: text(100).nullable().optional(),
    visibility: visibilitySchema.default('public'),
  })
  .strict();
export const socialLinkSchema = z
  .object({
    platform: text(40).min(1),
    url: httpsUrlSchema,
    handle: text(80).nullable().optional(),
    visibility: visibilitySchema.default('public'),
    position: z.number().int().min(0).max(49).default(0),
  })
  .strict();
export const signUpSchema = z
  .object({
    email: z.email(),
    password: z.string().min(8).max(128),
    username: usernameSchema,
    display_name: text(80).min(1),
    age_confirmed: z.literal(true),
    terms_accepted: z.literal(true),
  })
  .strict();
export const signInSchema = z
  .object({ email: z.email(), password: z.string().min(1).max(128) })
  .strict();
export const pageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(24),
  cursor: z.string().max(512).optional(),
});
export const catalogQuerySchema = pageQuerySchema.extend({
  q: text(100).default(''),
  manufacturer_id: uuidSchema.optional(),
  category_id: uuidSchema.optional(),
  sort: z
    .enum(['relevance', 'name', 'newest', 'rating', 'price', 'price_low', 'price_high'])
    .default('name'),
  min_rating: z.coerce.number().min(0).max(5).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default('USD'),
  min_price: z.coerce.number().nonnegative().optional(),
  max_price: z.coerce.number().nonnegative().optional(),
  has_3d: z.boolean().optional(),
  muscle_slugs: z
    .array(
      z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .max(80),
    )
    .max(30)
    .default([]),
  max_width_mm: z.coerce.number().finite().positive().optional(),
  max_depth_mm: z.coerce.number().finite().positive().optional(),
  max_height_mm: z.coerce.number().finite().positive().optional(),
});
export const purchaseSchema = z
  .object({
    amount: z.number().finite().nonnegative().max(10000000).nullable().default(null),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .default('USD'),
    source: text(200).nullable().default(null),
    purchased_on: z.iso.date().nullable().default(null),
    private_notes: text(4000).default(''),
  })
  .strict();
export const ownedItemSchema = z
  .object({
    gym_id: uuidSchema,
    product_id: uuidSchema.nullable().default(null),
    variant_id: uuidSchema.nullable().default(null),
    custom_name: text(160).nullable().default(null),
    quantity: z.number().int().min(1).max(1000).default(1),
    condition: z.enum(['new', 'like_new', 'good', 'fair', 'needs_repair']).default('good'),
    ownership_status: z.enum(['owned', 'wishlist', 'previously_owned']).default('owned'),
    visibility: visibilitySchema.default('public'),
    notes: text(4000).default(''),
    position: z.number().int().min(0).max(10000).default(0),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (!value.product_id && !value.custom_name)
      ctx.addIssue({
        code: 'custom',
        path: ['custom_name'],
        message: 'Choose a catalog product or name your custom item.',
      });
    if (value.variant_id && !value.product_id)
      ctx.addIssue({
        code: 'custom',
        path: ['variant_id'],
        message: 'A variant requires a catalog product.',
      });
  });
export const reviewSchema = z
  .object({
    product_id: uuidSchema,
    variant_id: uuidSchema.nullable().default(null),
    rating: z.number().min(0.5).max(5).multipleOf(0.5),
    title: text(160).min(3),
    body: text(10000).min(20),
    pros: text(2000).default(''),
    cons: text(2000).default(''),
    ownership_declaration: z.enum(['owned', 'used', 'considering']).default('used'),
    affiliation: text(500).default('None'),
  })
  .strict();
export const postSchema = z
  .object({ body: text(5000).min(1), visibility: visibilitySchema.default('public') })
  .strict();
export const commentSchema = z
  .object({
    post_id: uuidSchema,
    parent_id: uuidSchema.nullable().default(null),
    body: text(2000).min(1),
  })
  .strict();
/** Opaque, processed member-photo pointer. Flexes never accept arbitrary photo URLs. */
export const storyPhotoUrlSchema = z
  .string()
  .regex(
    /^https:\/\/media\.garagegymbros\.invalid\/[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/,
    'Upload a photo before attaching it to a Flex.',
  );
export const storySchema = z
  .object({
    body: text(500).default(''),
    visibility: visibilitySchema.default('public'),
    image_url: storyPhotoUrlSchema.nullable().optional(),
    duration_hours: z.union([z.literal(24), z.literal(48)]).optional(),
  })
  .strict()
  .refine((value) => Boolean(value.body || value.image_url), {
    message: 'Add a photo or write something before sharing a Flex.',
  });
export const topicSchema = z
  .object({
    category_id: uuidSchema,
    title: text(180).min(5),
    body: text(15000).min(10),
    kind: z.enum(['question', 'discussion']).default('question'),
    visibility: visibilitySchema.default('public'),
    tags: z.array(text(30).min(1)).max(8).default([]),
  })
  .strict();
export const replySchema = z
  .object({
    topic_id: uuidSchema,
    parent_id: uuidSchema.nullable().default(null),
    body: text(10000).min(1),
  })
  .strict();
export const tradeSchema = z
  .object({
    owned_item_id: uuidSchema,
    category: text(100).min(1).default('Equipment'),
    condition: z.enum(['new', 'like_new', 'good', 'fair', 'needs_repair']).default('good'),
    visibility: visibilitySchema.default('public'),
    title: text(160).min(3),
    description: text(5000).min(10),
    desired_trade: text(1000).min(1),
    approximate_location: text(120).min(2),
    asking_value: z.number().finite().nonnegative().max(10000000).nullable().default(null),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .default('USD'),
    expires_at: z.iso
      .datetime()
      .refine((v) => Date.parse(v) > Date.now(), 'Choose a future expiration.')
      .refine(
        (v) => Date.parse(v) < Date.now() + 90 * 86400000,
        'Listings can run for up to 90 days.',
      ),
  })
  .strict();
export const tradeStatusSchema = z.enum([
  'available',
  'reserved',
  'completed',
  'withdrawn',
  'hidden',
]);
export const messageSchema = z
  .object({
    conversation_id: uuidSchema,
    body: text(4000).min(1),
    client_nonce: uuidSchema.optional(),
  })
  .strict();
export const reportSchema = z
  .object({
    target_type: z.enum([
      'profile',
      'post',
      'comment',
      'story',
      'review',
      'message',
      'forum_topic',
      'forum_reply',
      'trade',
    ]),
    target_id: uuidSchema,
    reason: z.enum(['harassment', 'spam', 'scam', 'unsafe', 'privacy', 'other']),
    details: text(4000).default(''),
  })
  .strict();
export const moderationSchema = z
  .object({
    target_type: z.enum([
      'profile',
      'post',
      'comment',
      'story',
      'review',
      'forum_topic',
      'forum_reply',
      'trade',
    ]),
    target_id: uuidSchema,
    new_status: text(30).min(1),
    reason: text(1000).min(10),
  })
  .strict();
export const roleAssignmentSchema = z
  .object({ target_user: uuidSchema, new_role: accountRoleSchema, reason: text(1000).min(10) })
  .strict();
export const accountActionSchema = z.object({ kind: z.enum(['export', 'deletion']) }).strict();
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;
export type OwnedItemInput = z.infer<typeof ownedItemSchema>;
export type ReviewInput = z.infer<typeof reviewSchema>;
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;
export type PostInput = z.infer<typeof postSchema>;
export type TradeInput = z.infer<typeof tradeSchema>;
export type SocialLinkInput = z.infer<typeof socialLinkSchema>;

export const settingsUpdateSchema = z
  .object({
    first_name: text(80).nullable(),
    last_name: text(80).nullable(),
    notification_preferences: z.record(z.string().max(80), z.boolean()),
    catalog_view: z.enum(['grid', 'list']),
  })
  .strict();
export const gymEstablishedYearSchema = z.number().int().min(1950).max(2100).nullable().optional();
export const gymUpdateSchema = z
  .object({
    name: text(100).min(1),
    description: text(4000).default(''),
    visibility: visibilitySchema.default('private'),
    status: z.enum(['active', 'archived']).default('active'),
    established_year: gymEstablishedYearSchema,
  })
  .strict();
export const catalogSubmissionSchema = z
  .object({
    item_id: uuidSchema.nullable().default(null),
    product_id: uuidSchema.nullable().default(null),
    details: text(5000).min(10),
  })
  .strict();
export const catalogProductSchema = z
  .object({
    manufacturer_id: uuidSchema,
    category_id: uuidSchema.nullable().default(null),
    slug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .max(200),
    name: text(200).min(1),
    model: text(160).nullable().default(null),
    description: text(5000).default(''),
    specifications: z.record(z.string().max(100), z.json()).default({}),
    source_url: httpsUrlSchema,
    status: z.enum(['draft', 'published', 'retired']).default('draft'),
    width_mm: z.number().finite().positive().nullable().default(null),
    depth_mm: z.number().finite().positive().nullable().default(null),
    height_mm: z.number().finite().positive().nullable().default(null),
  })
  .strict();
export const moderationCaseSchema = z
  .object({
    report_id: uuidSchema.nullable().default(null),
    assigned_to: uuidSchema.nullable().default(null),
    target_type: z.enum([
      'profile',
      'post',
      'comment',
      'story',
      'review',
      'message',
      'forum_topic',
      'forum_reply',
      'trade',
    ]),
    target_id: uuidSchema,
    status: z.enum(['open', 'reviewing', 'resolved', 'appealed']).default('open'),
    outcome: text(2000).nullable().default(null),
  })
  .strict();
